/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * EventPicker — the event dropdown on both Check-in tabs
 * -----------------------------------------------------
 * A button showing the chosen event (date tile, title, status, start time)
 * that opens a searchable list grouped Open now / Finished / Upcoming. On a
 * wide screen the list drops down under the button; on a phone it is a
 * bottom sheet.
 *
 * History passes every event. The scanner passes only the events open now,
 * plus `all` — an "All open events" choice on top, shown when `selected` is
 * null. `note` adds a short figure after the start time (e.g. "3 in").
 *
 * No requests: the events come from the caller.
 */

import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { clockIn } from './checkInRules';
import { utcToWallClock } from '../../../utils/zonedDateTime';
import { formatDateTime } from '../../athlohive/formatDateTime';

const GROUP_LABELS = [['open', 'Open now'], ['finished', 'Finished'], ['upcoming', 'Upcoming']];
const STATUS_LABEL = { open: 'Open now', finished: 'Finished', upcoming: 'Upcoming' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The day-and-month tile the Events list uses, in the event's own zone. */
function DateTile({ event, size = 'md' }) {
  const wall = event ? utcToWallClock(event.start_datetime, event.timezone) : '';
  const day = wall ? Number(wall.slice(8, 10)) : '—';
  const month = wall ? MONTHS[Number(wall.slice(5, 7)) - 1] : '';
  return (
    <span className={`ochh-date is-${size}`} aria-hidden="true">
      <b>{day}</b>
      <span>{month}</span>
    </span>
  );
}

/** The "All open events" tile: how many events, in place of a date. */
function CountTile({ count, size = 'md' }) {
  return (
    <span className={`ochh-date is-${size}`} aria-hidden="true">
      <b>{count}</b>
      <span>{count === 1 ? 'event' : 'events'}</span>
    </span>
  );
}

function EventPicker({ groups, selected, onSelect, all = null, note = '', title = 'Your events' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const boxRef = useRef(null);
  const searchId = useId();
  const groupOf = useMemo(() => {
    const map = {};
    Object.entries(groups).forEach(([key, list]) => list.forEach((event) => { map[event.id] = key; }));
    return map;
  }, [groups]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    const onClick = (event) => { if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false); };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick); };
  }, [open]);

  const needle = search.trim().toLowerCase();
  const matches = (event) => !needle || String(event.title || '').toLowerCase().includes(needle);
  const choose = (id) => { onSelect(id); setOpen(false); setSearch(''); };
  const eventCount = GROUP_LABELS.reduce((n, [key]) => n + groups[key].length, 0);
  const allMatches = all && (!needle || 'all open events'.includes(needle));
  const anyMatch = allMatches || GROUP_LABELS.some(([key]) => groups[key].some(matches));
  const showsAll = all && !selected;

  // Arrow keys move between options; from the search box, Down enters the list.
  const onListKey = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const options = [...(boxRef.current?.querySelectorAll('.ochh-option') || [])];
    if (!options.length) return;
    event.preventDefault();
    const at = options.indexOf(document.activeElement);
    const next = event.key === 'ArrowDown' ? Math.min(at + 1, options.length - 1) : Math.max(at - 1, 0);
    options[at === -1 ? 0 : next].focus();
  };

  const tick = (on) => (on
    ? <i className="bi bi-check2 ochh-tick" aria-hidden="true" />
    : <span className="ochh-tick" aria-hidden="true" />);

  return (
    <div className="ochh-picker" ref={boxRef}>
      <button
        type="button"
        className="ochh-pickbtn"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {showsAll ? <CountTile count={eventCount} /> : <DateTile event={selected} />}
        <span className="ochh-pickmain">
          <b>{showsAll ? 'All open events' : (selected ? selected.title : 'Choose an event')}</b>
          {(showsAll || selected) && (
            <span className="ochh-pickmeta">
              <span className={`ochh-pill is-${showsAll ? 'open' : groupOf[selected.id]}`}>
                <span className={`ochh-dot is-${showsAll ? 'open' : groupOf[selected.id]}`} />
                {showsAll ? STATUS_LABEL.open : STATUS_LABEL[groupOf[selected.id]]}
              </span>
              {!showsAll && clockIn(selected.start_datetime, selected.timezone)}
              {note && <span className="org-num">{showsAll ? note : `· ${note}`}</span>}
            </span>
          )}
        </span>
        <i className={`bi bi-chevron-down ochh-chev${open ? ' is-open' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <>
          <div className="ochh-scrim" aria-hidden="true" onClick={() => setOpen(false)} />
          <div className="ochh-panel" role="dialog" aria-label="Choose an event" onKeyDown={onListKey}>
            <div className="ochh-panelhead">
              <b>Choose an event</b>
              <button type="button" className="ochk-iconbtn" onClick={() => setOpen(false)} aria-label="Close">
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>
            <div className="ochh-paneltitle">
              <b>{title}</b>
              <span>{eventCount}</span>
            </div>
            <div className="ochh-searchwrap">
              <i className="bi bi-search" aria-hidden="true" />
              <label className="sr-only-org" htmlFor={searchId}>Search your events</label>
              <input
                id={searchId}
                className="ochh-search"
                type="search"
                placeholder="Search your events"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                autoFocus
              />
            </div>
            <ul className="ochh-list" role="listbox" aria-label={title}>
              {allMatches && (
                <li role="presentation">
                  <ul role="presentation">
                    <li role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={!selected}
                        className="ochh-option"
                        onClick={() => choose(null)}
                      >
                        <CountTile count={eventCount} size="sm" />
                        <span className="ochh-optmain">
                          <b>All open events</b>
                          <span><span className="ochh-dot is-open" aria-hidden="true" />Every scan, from every open event</span>
                        </span>
                        {tick(!selected)}
                      </button>
                    </li>
                  </ul>
                </li>
              )}
              {GROUP_LABELS.map(([key, label]) => {
                const list = groups[key].filter(matches);
                if (!list.length) return null;
                return (
                  <li key={key} role="presentation">
                    <p className="org-lab ochh-group">{label}<span>{list.length}</span></p>
                    <ul role="presentation">
                      {list.map((event) => (
                        <li key={event.id} role="presentation">
                          <button
                            type="button"
                            role="option"
                            aria-selected={selected?.id === event.id}
                            className="ochh-option"
                            onClick={() => choose(event.id)}
                          >
                            <DateTile event={event} size="sm" />
                            <span className="ochh-optmain">
                              <b>{event.title}</b>
                              <span>
                                <span className={`ochh-dot is-${key}`} aria-hidden="true" />
                                {formatDateTime(event.start_datetime, event.timezone)}
                              </span>
                            </span>
                            {tick(selected?.id === event.id)}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
              {!anyMatch && <li className="ochh-none">No events match “{search}”.</li>}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

export default EventPicker;
